// SEO-friendly product paths: /catalog/<keyword-slug>--<sku>
// The "--" separator never appears in a slugified title. SKUs keep their true
// value in the database; only the PATH form is encoded:
//   "/" inside SKUs (e.g. XM105/35) -> "~"  (paths stay single-segment)
//   "." inside SKUs (e.g. QH5447-8.5) -> "_" (a literal dot makes the hosting
//   edge treat the URL as a static file, so crawlers never see the product
//   page; "_" is safe because no supplier SKU contains an underscore —
//   verified across the full catalog 2026-10-20, and the import warns if one
//   ever appears)
// Legacy sku-only paths (/catalog/TB21-8) and OLD DOTTED paths keep working via
// skuFromSlug (a suffix without "_" decodes to itself).
import { useEffect as useReactEffect } from "react";

// Single source of truth for SKU <-> path encoding.
export const encodeSkuForPath = (sku) => (sku || "").replace(/\//g, "~").replace(/\./g, "_");
export const decodeSkuFromPath = (s) => (s || "").replace(/~/g, "/").replace(/_/g, ".");

export const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");

export const productPath = (p) =>
  `/catalog/${slugify(p.seo_title || p.name)}--${encodeSkuForPath(p.sku)}`;

export const skuFromSlug = (slug) => {
  const idx = slug.lastIndexOf("--");
  const raw = idx >= 0 ? slug.slice(idx + 2) : slug;
  return decodeSkuFromPath(raw);
};

// Rebase a slug's keyword prefix onto a different SKU (variant switching keeps
// the parent's keywords in the URL). The parent's own size/length token is
// stripped so the URL never says "size-7" while viewing size 4.
export const slugWithSku = (currentSlug, fallbackTitle, sku) => {
  const idx = currentSlug.lastIndexOf("--");
  let prefix = idx >= 0 ? currentSlug.slice(0, idx) : slugify(fallbackTitle);
  prefix = prefix.replace(/-(size-[\d.]+|[\d.]+in)$/, "");
  return `/catalog/${prefix}--${encodeSkuForPath(sku)}`;
};

// Customer-facing style number: the vendor SKU wrapped in Gold Time branding.
// The vendor SKU stays canonical in URLs, the Google feed and the daily sync —
// this wrapper is display-only and reversible (strip "G-"/"-T").
export const displaySku = (sku) => `G-${(sku || "").replace(/~/g, "/")}-T`;

// ---------- Central per-route SEO (single source of truth for head tags) ----------

export const SITE_ORIGIN = "https://mygoldtime.com";
export const DEFAULT_TITLE = "Gold Time Jewelry | Fine Jewelry, Diamonds & Watches — Katy, TX";
export const DEFAULT_DESCRIPTION =
  "Shop gold chains, diamond rings, name plates & lab-grown diamonds online. Family-owned Texas jeweler since 1991 with showrooms in Katy, Copperas Cove & Killeen. Insured US shipping.";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/assets/hero.jpg`;

const upsertTag = (tagName, keyAttr, keyValue, attrs) => {
  let el = document.head.querySelector(`${tagName}[${keyAttr}="${keyValue}"]`);
  if (!el) {
    el = document.createElement(tagName);
    el.setAttribute(keyAttr, keyValue);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const setRobots = (noindex) => {
  let el = document.head.querySelector('meta[name="robots"][data-seo="route"]');
  if (noindex) {
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute("data-seo", "route");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, follow");
  } else if (el) {
    el.remove();
  }
};

let jsonLdEl = null;
const setJsonLd = (data) => {
  if (jsonLdEl) {
    jsonLdEl.remove();
    jsonLdEl = null;
  }
  if (data) {
    jsonLdEl = document.createElement("script");
    jsonLdEl.setAttribute("type", "application/ld+json");
    jsonLdEl.setAttribute("data-seo", "route");
    jsonLdEl.textContent = JSON.stringify(data);
    document.head.appendChild(jsonLdEl);
  }
};

// Every indexable route calls this. canonicalPath must be the canonical,
// self-referencing path (no filter/sort/tracking params).
export function useSEO({ title, description, canonicalPath, noindex = false, ogType = "website", ogImage, jsonLd }) {
  const path = canonicalPath || "/";
  const fullTitle = title || DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = `${SITE_ORIGIN}${path}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useReactEffect(() => {
    document.title = fullTitle;
    upsertTag("meta", "name", "description", { content: desc });
    upsertTag("link", "rel", "canonical", { href: url });
    setRobots(noindex);
    upsertTag("meta", "property", "og:title", { content: fullTitle });
    upsertTag("meta", "property", "og:description", { content: desc });
    upsertTag("meta", "property", "og:url", { content: url });
    upsertTag("meta", "property", "og:type", { content: ogType });
    upsertTag("meta", "property", "og:image", { content: image });
    upsertTag("meta", "name", "twitter:card", { content: "summary_large_image" });
    upsertTag("meta", "name", "twitter:title", { content: fullTitle });
    upsertTag("meta", "name", "twitter:description", { content: desc });
    upsertTag("meta", "name", "twitter:image", { content: image });
    setJsonLd(jsonLd || null);
    return () => {
      document.title = DEFAULT_TITLE;
      setRobots(false);
      setJsonLd(null);
    };
  }, [fullTitle, desc, url, ogType, image, JSON.stringify(jsonLd || null)]);
}
